import type { AgentPrepared, AgentToolDef } from './tool-defs';

/**
 * Confirm-before-write gate.
 *
 * A write tool call from the model only opens a ticket. The call runs only with an
 * Approval, and an Approval exists only when the user clicked the confirm card's
 * button (the store calls approve() from that click handler). Approvals are objects
 * registered in a module-private WeakSet, so nothing the model can produce (tool
 * input, a forged `confirmed: true`, a replayed message) counts as one. Each
 * Approval is single-use and bound to one tool call id and one tool name.
 *
 * Pure: no Nuxt, no network.
 */

export interface ConfirmTicket {
  readonly toolCallId: string;
  readonly toolName: string;
  readonly def: AgentToolDef;
  /** Input as validated by the tool's zod schema (unknown keys stripped). */
  readonly input: unknown;
  readonly prepared: AgentPrepared;
}

export interface Approval {
  readonly toolCallId: string;
  readonly toolName: string;
}

const issued = new WeakSet<object>();
const used = new WeakSet<object>();

/** True, once, for an Approval that this module issued for this tool. Consumes it. */
export const consumeApproval = (approval: unknown, def: Pick<AgentToolDef, 'name'>): boolean => {
  if (!approval || typeof approval !== 'object') return false;
  if (!issued.has(approval) || used.has(approval)) return false;
  if ((approval as Approval).toolName !== def.name) return false;
  used.add(approval);
  return true;
};

export const createConfirmGate = () => {
  const open = new Map<string, ConfirmTicket>();

  return {
    /** Registers a write call waiting for the user. Replaces a ticket with the same id. */
    request(ticket: ConfirmTicket): ConfirmTicket {
      const frozen = Object.freeze({ ...ticket });
      open.set(ticket.toolCallId, frozen);
      return frozen;
    },
    /** Called only from the confirm card's click handler. Single use. */
    approve(ticket: ConfirmTicket): Approval | null {
      const current = open.get(ticket.toolCallId);
      if (!current || current !== ticket) return null;
      open.delete(ticket.toolCallId);
      const approval: Approval = Object.freeze({ toolCallId: ticket.toolCallId, toolName: ticket.toolName });
      issued.add(approval);
      return approval;
    },
    /** Cancel / decline / reset. */
    cancel(toolCallId: string): void {
      open.delete(toolCallId);
    },
    clear(): void {
      open.clear();
    },
    isOpen(toolCallId: string): boolean {
      return open.has(toolCallId);
    },
    size(): number {
      return open.size;
    },
  };
};

export type ConfirmGate = ReturnType<typeof createConfirmGate>;
